import { useMemo, useState } from 'react';
import { Input, message, Popconfirm, Tooltip } from 'antd';
import { Search, Edit2, Trash2, Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Pipeline, PipelineType, PIPELINE_TYPE_LABELS } from '../types';
import { useCreatePipeline } from '../hooks/useCreatePipeline';
import { useDeletePipeline } from '../hooks/useDeletePipeline';
import { getPipelineTypesForRole, canCreatePipeline } from '../hooks/usePipelinesList';
import { canManagePipelineStages } from '../hooks/usePipelineStages';

const NO_PERMISSION_MESSAGE =
  'Você não tem permissão para criar novas pipelines.';

interface PipelinePopoverProps {
  pipelines: Pipeline[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEditPipeline: (pipeline: Pipeline) => void;
  onEditStages: () => void;
  onClose: () => void;
}

export function PipelinePopover({
  pipelines,
  selectedId,
  onSelect,
  onEditPipeline,
  onEditStages,
  onClose,
}: PipelinePopoverProps) {
  const { profile } = useAuth();
  const [newPipelineName, setNewPipelineName] = useState('');

  const canCreate = useMemo(() => canCreatePipeline(profile?.role), [profile?.role]);
  const canManageStages = useMemo(() => canManagePipelineStages(profile?.role), [profile?.role]);

  const availableTypes = useMemo(
    () => getPipelineTypesForRole(profile?.role),
    [profile?.role]
  );
  const newPipelineType: PipelineType = availableTypes[0] ?? 'qualificacao';

  const createPipeline = useCreatePipeline();
  const deletePipeline = useDeletePipeline();

  const handleCreatePipeline = async () => {
    if (!canCreate) return;
    const nome = newPipelineName.trim();
    if (!nome) return;

    try {
      const created = await createPipeline.mutateAsync({ nome, tipo: newPipelineType });
      setNewPipelineName('');
      onSelect(created.id);
      message.success('Pipeline criada');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Erro ao criar pipeline');
    }
  };

  return (
    <div className="w-96 max-h-[520px] flex flex-col bg-white dark:bg-zinc-900 rounded-lg shadow-xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3.5 border-b border-gray-100 dark:border-zinc-800/50 bg-gray-50/50 dark:bg-zinc-800/30">
        <h3 className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-widest">Pipelines</h3>
      </div>

      {/* Lista de pipelines */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-2 space-y-1">
          {pipelines.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-400">Nenhuma pipeline criada</p>
            </div>
          ) : (
            pipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className={`group flex items-center gap-3 px-3 py-3 rounded-lg transition-all cursor-default ${
                  selectedId === pipeline.id
                    ? 'bg-blue-50 dark:bg-blue-950/50 border border-blue-100 dark:border-blue-900/50'
                    : 'hover:bg-gray-50 dark:hover:bg-zinc-800/50 border border-transparent'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    onSelect(pipeline.id);
                    onClose();
                  }}
                  className="flex-1 text-left min-w-0"
                >
                  <div className={`font-semibold text-sm truncate transition-colors ${
                    selectedId === pipeline.id
                      ? 'text-blue-700 dark:text-blue-300'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {pipeline.nome}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {PIPELINE_TYPE_LABELS[pipeline.tipo] || pipeline.tipo}
                  </div>
                </button>

                <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canManageStages && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                        onEditStages();
                      }}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors"
                      title="Editar etapas"
                    >
                      <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditPipeline(pipeline);
                    }}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                    title="Editar pipeline"
                  >
                    <Edit2 className="w-4.5 h-4.5" />
                  </button>

                  {!pipeline.fixa && (
                    <Popconfirm
                      title="Deletar pipeline?"
                      description="Essa ação não pode ser desfeita"
                      onConfirm={() => {
                        deletePipeline.mutate(pipeline.id, {
                          onSuccess: () => {
                            message.success('Pipeline deletada');
                            if (selectedId === pipeline.id && pipelines.length > 1) {
                              onSelect(pipelines[0]?.id || '');
                            }
                          },
                          onError: (err) => {
                            message.error(err instanceof Error ? err.message : 'Erro ao deletar');
                          }
                        });
                      }}
                      okText="Deletar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                    >
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                        title="Deletar pipeline"
                      >
                        <Trash2 className="w-4.5 h-4.5" />
                      </button>
                    </Popconfirm>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Criar nova pipeline */}
      <div className="px-4 py-3.5 border-t border-gray-100 dark:border-zinc-800/50 bg-gray-50/50 dark:bg-zinc-800/30 space-y-3">
        <label className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-widest block">Criar novo funil</label>
        <Tooltip title={canCreate ? undefined : NO_PERMISSION_MESSAGE}>
          <div className={`space-y-2.5 ${canCreate ? '' : 'cursor-not-allowed'}`}>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Nome do funil"
                value={newPipelineName}
                onChange={(e) => setNewPipelineName(e.target.value)}
                onPressEnter={handleCreatePipeline}
                size="small"
                disabled={!canCreate || createPipeline.isPending}
              />
              <button
                type="button"
                onClick={handleCreatePipeline}
                disabled={!canCreate || !newPipelineName.trim() || createPipeline.isPending}
                className="flex-shrink-0 px-3 h-8 flex items-center justify-center rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                {createPipeline.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </Tooltip>
      </div>
    </div>
  );
}

export default PipelinePopover;
